import LineGlassMartiniAltIcon from 'LuxKit/Icons/line/LineGlassMartiniAltIcon'
import LineGlobeAltIcon from 'LuxKit/Icons/line/LineGlobeAltIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineRefundIcon from 'LuxKit/Icons/line/LineRefundIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineTagAltIcon from 'LuxKit/Icons/line/LineTagAltIcon'
import type SvgIcon from 'LuxKit/SvgIcon'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import { isNonNullable } from 'lib/array/arrayUtils'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import type { MessageDescriptor } from 'react-intl'
import { defineMessages } from 'react-intl'
import { createSelector } from 'reselect'

export interface USPContentItem {
  Icon: typeof SvgIcon
  bodyMessage: MessageDescriptor
}

const uspMessages = defineMessages({
  loyaltyPoints: {
    id: 'gdl4WpEape-6Vaib4rVm',
    defaultMessage: 'Earn points on every booking and use them towards future escapes.',
    description: 'Front page USP selling point about earning loyalty points on bookings',
  },
  exclusiveOffers: {
    id: 'ZT9LynmJr_vmjiCsU4jw',
    defaultMessage: 'Unlock exclusive offers you won\u2019t find elsewhere.',
    description: 'Front page USP selling point about exclusive member-only travel offers',
  },
  bonusInclusions: {
    id: '29mS3NDe5GI_JR0kPKXm',
    defaultMessage: 'Enjoy bonus handpicked inclusions, curated just for you',
    description: 'Front page USP selling point about curated bonus inclusions with bookings',
  },
  discoverMore: {
    id: 'UmHHkfg0Y4EeaydZgZNK',
    defaultMessage: 'Discover thousands of flights, hotels, tours, cruises & more',
    description: 'Front page USP selling point about the breadth of travel products available',
  },
  flexibleBooking: {
    id: 'Hn327HYVieDVbCPkET3J',
    defaultMessage: 'Flexible booking options for peace of mind',
    description: 'Front page USP selling point about flexible booking and cancellation options',
  },
  support247: {
    id: 'sL26ce0KbG6xZcPK9LbQ',
    defaultMessage: 'Our customer service team is available 24/7',
    description: 'Front page USP selling point about round-the-clock customer service availability',
  },
  supportLimited: {
    id: 'RVg2V78hIh9PPjdLVSHt',
    defaultMessage: 'Our customer service team is available 9am to 7pm every day',
    description: 'Front page USP selling point about daily customer service availability hours',
  },
})

const getUSPContent = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => getIsLoyaltyEnabled(state),
  (
    currentRegionCode,
    isLoyaltyEnabled,
  ): Array<USPContentItem> => {
    const no247Support = NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode)

    return [
      isLoyaltyEnabled ? {
        Icon: LineStarIcon,
        bodyMessage: uspMessages.loyaltyPoints,
      } : undefined,
      {
        Icon: LineTagAltIcon,
        bodyMessage: uspMessages.exclusiveOffers,
      },
      {
        Icon: LineGlassMartiniAltIcon,
        bodyMessage: uspMessages.bonusInclusions,
      },
      {
        Icon: LineGlobeAltIcon,
        bodyMessage: uspMessages.discoverMore,
      },
      {
        Icon: LineRefundIcon,
        bodyMessage: uspMessages.flexibleBooking,
      },
      !no247Support ? {
        Icon: LinePhoneIcon,
        bodyMessage: uspMessages.support247,
      } : undefined,
      no247Support ? {
        Icon: LinePhoneIcon,
        bodyMessage: uspMessages.supportLimited,
      } : undefined,
    ].filter(isNonNullable)
  },
)

export default getUSPContent
